import { html, nothing, svg } from '../application_element/element.js';
import ChartElement from '../chart_element/element.js';
import { formatValue } from '../../lib/chart_format.js';
import translations from './translations.js';
import style from './style.lit.css';

const CENTER = 50;
const RADIUS = 49;
const TURN = Math.PI * 2;

/** Trims the path data to what the geometry is actually accurate to. */
const round = value => Number(value.toFixed(3));

/**
 * A series as a pie or donut, with a legend that is always present — past a few
 * buckets a slice cannot be identified by colour alone.
 *
 * Only for a `series`: a timeseries has no total to take shares of, and slices
 * carry no order.
 *
 * @prop hole - Inner radius as a fraction of the outer; 0 draws a full pie ( number, default: 0.6 )
 * @prop total - Print the total in the middle of a donut ( boolean, default: true )
 * @prop totalLabel - Names the figure in the middle ( string )
 *
 * @cssprop [--dial-size: 180px] - Width of the dial.
 * @cssprop [--legend-min-width: 180px] - Below this the legend wraps under the dial.
 * @cssprop [--legend-max-width: 380px] - Cap, so values stay next to their labels.
 */
export default class PieChart extends ChartElement {
  static properties = {
    hole: { type: Number },
    total: { type: Boolean },
    totalLabel: { type: String },
  };

  static translations = translations;

  static styles = [ChartElement.styles, style];

  constructor() {
    super();
    this.hole = 0.6;
    this.total = true;
  }

  /** Cumulative fractions of the total, empty buckets dropped. */
  get slices() {
    const total = this.totalValue;
    let start = 0;

    return this.rows
      .map((row, index) => {
        const fraction = total > 0 ? this.numberOf(row) / total : 0;
        const slice = {
          row,
          fraction,
          start,
          end: start + fraction,
          color: this.colorOf(row, index),
        };

        start += fraction;

        return slice;
      })
      .filter(slice => slice.fraction > 0);
  }

  get innerRadius() {
    return RADIUS * Math.min(Math.max(this.hole, 0), 0.95);
  }

  /** Clockwise from twelve o'clock, which is where a reader starts. */
  point(fraction, radius) {
    const angle = fraction * TURN - Math.PI / 2;

    return [
      round(CENTER + radius * Math.cos(angle)),
      round(CENTER + radius * Math.sin(angle)),
    ];
  }

  ring(radius) {
    const top = round(CENTER - radius);
    const bottom = round(CENTER + radius);

    return `M ${CENTER} ${top} A ${radius} ${radius} 0 1 1 ${CENTER} ${bottom} A ${radius} ${radius} 0 1 1 ${CENTER} ${top} Z`;
  }

  // A single bucket owns the whole circle, and an arc that ends where it starts
  // draws nothing. Two rings punched with `evenodd` is the same shape.
  wholePath() {
    const inner = this.innerRadius;

    return inner > 0
      ? `${this.ring(RADIUS)} ${this.ring(inner)}`
      : this.ring(RADIUS);
  }

  slicePath({ start, end, fraction }) {
    if (fraction >= 1) return this.wholePath();

    const inner = this.innerRadius;
    const large = fraction > 0.5 ? 1 : 0;
    const [x0, y0] = this.point(start, RADIUS);
    const [x1, y1] = this.point(end, RADIUS);

    if (inner <= 0)
      return `M ${CENTER} ${CENTER} L ${x0} ${y0} A ${RADIUS} ${RADIUS} 0 ${large} 1 ${x1} ${y1} Z`;

    const [x2, y2] = this.point(end, inner);
    const [x3, y3] = this.point(start, inner);

    return `M ${x0} ${y0} A ${RADIUS} ${RADIUS} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${inner} ${inner} 0 ${large} 0 ${x3} ${y3} Z`;
  }

  shareOf(row) {
    const total = this.totalValue;

    return total > 0 ? this.numberOf(row) / total : 0;
  }

  percentOf(row) {
    return formatValue(this.shareOf(row), {
      locale: this.i18n.locale,
      format: 'percent',
      precision: 1,
    });
  }

  renderDial() {
    const slices = this.slices;

    return html`
      <div class="dial">
        <svg
          viewBox="0 0 100 100"
          role="img"
          aria-label=${this.caption || this.t('chart')}>
          <!-- Behind the slices, so an all-zero series still reads as a chart
               rather than as a rendering failure. -->
          <path class="track" d=${this.wholePath()} fill-rule="evenodd"></path>
          ${slices.map(
            slice => svg`
              <path
                class="slice"
                d=${this.slicePath(slice)}
                style="fill: ${slice.color}"
                fill-rule="evenodd">
                <title>${this.descriptionOf(slice.row)}</title>
              </path>
            `
          )}
        </svg>
        ${this.total && this.innerRadius > 0
          ? html`
              <div class="middle" aria-hidden="true">
                <span class="figure">
                  ${formatValue(this.totalValue, {
                    locale: this.i18n.locale,
                    format: this.format,
                    precision: this.precision,
                  })}
                </span>
                <span class="figure-label"
                  >${this.totalLabel || this.t('total')}</span
                >
              </div>
            `
          : nothing}
      </div>
    `;
  }

  renderLegend() {
    return html`
      <ul class="legend">
        ${this.rows.map(
          (row, index) => html`
            <li>
              <span
                class="swatch"
                style="background-color: ${this.colorOf(row, index)}"></span>
              <span class="name" title=${this.labelOf(row)}
                >${this.labelOf(row)}</span
              >
              <span class="amount">${this.valueOf(row)}</span>
              <span class="percent">${this.percentOf(row)}</span>
            </li>
          `
        )}
      </ul>
    `;
  }

  render() {
    if (this.empty) return this.renderEmpty();

    return html`
      <figure>
        ${this.renderCaption()}
        <div class="chart">${this.renderDial()} ${this.renderLegend()}</div>
      </figure>
    `;
  }
}

PieChart.extendTranslationsAs('xs-pie-chart');

customElements.get('xs-pie-chart') ||
  customElements.define('xs-pie-chart', PieChart);
